import {setUserProperties, registerUserGroupEnsure} from './GAEvents';

// GA4 user_group segmentation. Priority: coach > school_of_chess >
// school_of_chess_expired > demo > other. 'guest' = not logged in,
// 'unknown' = logged in but flags not yet available this load.

const USER_GROUP_CACHE_KEY = 'ga_user_group';

export const deriveUserGroup = planDetails =>
  planDetails
    ? planDetails.is_coach
      ? 'coach'
      : planDetails.is_csoc_user
      ? 'school_of_chess'
      : planDetails.has_csoc_history
      ? 'school_of_chess_expired'
      : planDetails.is_demo_user
      ? 'demo'
      : 'other'
    : null;

// 'none' = nothing applied yet this load; 'fallback' = a placeholder value
// ('unknown'/'guest') was sent, so a later real value must still be applied
// and re-sent; 'real' = the actual derived group has been applied -- no need
// to re-send it on every subsequent event/pageview.
let appliedLevel: 'none' | 'fallback' | 'real' = 'none';

export const applyUserGroup = group => {
  try {
    localStorage.setItem(USER_GROUP_CACHE_KEY, group);
    setUserProperties({user_group: group});
    appliedLevel = 'real';
  } catch (e) {
    //pass
  }
};

export const ensureUserGroupApplied = () => {
  if (appliedLevel === 'real') {
    return;
  }
  try {
    const cached = localStorage.getItem(USER_GROUP_CACHE_KEY);
    if (cached) {
      setUserProperties({user_group: cached});
      appliedLevel = 'real';
      return;
    }
  } catch (e) {
    //pass
  }
  if (appliedLevel === 'fallback') {
    // Already sent the placeholder once this load -- don't re-send it on
    // every event/pageview while we wait for the real group to arrive.
    return;
  }
  try {
    // access_token (and the rest of the auth state) is only ever persisted
    // via localForage, which defaults to IndexedDB -- async, and not
    // reliably readable synchronously here. There's no plain,
    // synchronously-readable localStorage key that tells us whether the
    // user is logged in at this point in boot, so we don't guess: default
    // to 'unknown' and let applyUserGroup/resetUserGroupToGuest overwrite it
    // as soon as the real state is known.
    setUserProperties({user_group: 'unknown'});
    appliedLevel = 'fallback';
  } catch (e) {
    //pass
  }
};

// Registered by userPlanStore at module load (same hook pattern as
// GAEvents.registerUserGroupEnsure) so resetUserGroupToGuest can clear the
// persisted planDetails without a userGroup -> userPlanStore -> CommonUtils ->
// userGroup import cycle.
let clearPlanDetails: (() => void) | null = null;
export const registerPlanDetailsClear = (fn: () => void) => {
  clearPlanDetails = fn;
};

export const resetUserGroupToGuest = () => {
  try {
    // Clear the previous account's persisted plan flags too -- otherwise the
    // Application effect re-derives the stale bucket from the rehydrated
    // store on the next load and overwrites 'guest'.
    clearPlanDetails?.();
    localStorage.removeItem(USER_GROUP_CACHE_KEY);
    setUserProperties({user_group: 'guest'});
    // Not 'real' -- a subsequent login must still be able to upgrade this.
    appliedLevel = 'fallback';
  } catch (e) {
    //pass
  }
};

registerUserGroupEnsure(ensureUserGroupApplied);
